import { ArrowUpRight, MessageCircle } from "lucide-react";
import { PRODUCTS, BRAND } from "@/data/products";

const waLink = (productName) => {
  const num = (BRAND.whatsapp || "").replace(/[^0-9]/g, "");
  const msg = encodeURIComponent(
    `Hi ${BRAND.name}, I'd like to order ${productName}.`
  );
  return `https://wa.me/${num}?text=${msg}`;
};

const Products = () => {
  return (
    <div data-testid="products-page" className="bg-cream min-h-screen">
      {/* Header */}
      <section className="relative overflow-hidden pt-36 sm:pt-40 pb-16 sm:pb-20 border-b border-ink/10">
        <div className="absolute inset-0 pattern-bg opacity-60 pointer-events-none" />
        <div className="relative px-6 sm:px-12 lg:px-24">
          <p data-testid="products-eyebrow" className="eyebrow animate-fade-in-up">
            The Collection
          </p>
          <h1
            data-testid="products-title"
            className="font-display text-5xl sm:text-6xl md:text-7xl font-bold text-ink mt-6 max-w-3xl leading-[1] animate-fade-in-up delay-100"
          >
            Three jars worth the shelf.
          </h1>
          <p className="font-primary text-lg text-ink/60 mt-7 max-w-xl leading-relaxed animate-fade-in-up delay-200">
            Each spice is carefully sourced, processed with attention to quality,
            and sealed in freshness-focused packaging — so what you open tastes the
            way it should.
          </p>
        </div>
      </section>

      {/* Full-width alternating product sections */}
      {PRODUCTS.map((p, i) => (
        <section
          key={p.id}
          data-testid={`product-section-${p.id}`}
          className={`px-6 sm:px-12 lg:px-24 py-24 sm:py-32 border-b border-ink/10 ${
            i % 2 === 1 ? "pattern-bg" : ""
          }`}
        >
          <div
            className={`grid grid-cols-1 md:grid-cols-12 gap-12 items-center ${
              i % 2 === 1 ? "md:[direction:rtl]" : ""
            }`}
          >
            {/* Image */}
            <div className="md:col-span-6 relative overflow-hidden group [direction:ltr]">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-[68vh] object-cover hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute bottom-6 left-6 bg-coral px-5 py-2.5">
                <span className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-cream">
                  {p.label}
                </span>
              </div>
            </div>

            {/* Text */}
            <div className="md:col-span-5 md:col-start-8 [direction:ltr]">
              <p className="font-primary text-xs uppercase tracking-[0.2em] text-ink/40 italic">
                {p.botanical}
              </p>
              <h2 className="font-primary font-bold text-4xl sm:text-5xl text-ink mt-3">
                {p.name}
              </h2>

              <p className="font-primary text-lg text-ink/70 mt-7 leading-relaxed">
                {p.description}
              </p>

              {/* Tasting notes */}
              <div className="mt-9">
                <p className="eyebrow">Tasting Notes</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  {p.notes.map((n) => (
                    <span
                      key={n}
                      className="font-primary text-xs font-medium uppercase tracking-[0.12em] text-ink/70 border border-ink/20 px-4 py-2"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>

              <a
                href={waLink(p.name)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`product-buy-${p.id}`}
                className="btn-primary group mt-11"
              >
                <MessageCircle size={16} />
                Buy Now
                <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </div>
          </div>
        </section>
      ))}

      {/* Closing */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 sm:py-32 text-center">
        <h2 className="font-primary font-bold text-4xl sm:text-5xl text-ink max-w-2xl mx-auto leading-tight">
          More is coming, slowly.
        </h2>
        <p className="font-primary text-lg text-ink/60 mt-6 max-w-lg mx-auto leading-relaxed">
          We add a spice only when we find one worth the name. Until then, three
          is plenty.
        </p>
      </section>
    </div>
  );
};

export default Products;
